import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLearning } from 'src/hook/data/useLearning';
import { useSocial } from 'src/hook/data/useSocial';
import useUser from 'src/hook/data/useUser';
import { FriendSummaryData } from 'src/model/Social';
import { IconLocation } from 'src/utils/URLUtils';
import styles from './FriendSection.module.scss';
import { FriendsItem } from './FriendsItem/FriendsItem';

export const FriendSection = () => {
    const { t, i18n } = useTranslation();
    const { learningInfo } = useLearning();
    const { socialInfo, fetchSocialInfo } = useSocial();
    const { userInfo } = useUser();
    const [selectedTab, setSelectedTab] = useState('friends');
    const [friendsWithYou, setFriendsWithYou] = useState<FriendSummaryData[]>([]);
    const [peopleNearByWithYou, setPeopleNearByWithYou] = useState<FriendSummaryData[]>([]);
    const currentWeekProgress = learningInfo?.MyAccount?.MyProgress?.Weekly?.Items[3]?.Value;

    // Create the "you" object only when necessary values change
    const you = useMemo(() => ({
        Handle: '',
        Avatar: learningInfo?.MyAccount?.Summary?.ImageUrl,
        DisplayName: t('You'),
        SubscriptionId: '',
        CurrentWeekProgress: currentWeekProgress || 0,
    }), [learningInfo?.MyAccount?.Summary?.ImageUrl, currentWeekProgress, t]);

    // Create the "more" object once
    const more = useMemo(() => ({
        Handle: '',
        Avatar: IconLocation('celeb-more'),
        DisplayName: t('More'),
        SubscriptionId: '-1',
        CurrentWeekProgress: 0,
    }), [t]);

    // Fetch social info when needed
    useEffect(() => {
        if (!socialInfo && userInfo?.UserId) {
            fetchSocialInfo(userInfo?.UserId!, userInfo?.CountryCode);
        }
    }, [socialInfo, userInfo, fetchSocialInfo]);

    // Update the lists when socialInfo or you changes
    useEffect(() => {
        if (!socialInfo) return;

        const friends = socialInfo.Friends && socialInfo.Friends.length > 0
            ? [...socialInfo.Friends, you].sort((a, b) => b.CurrentWeekProgress - a.CurrentWeekProgress)
            : [];

        const people = socialInfo.PeopleNearBy && socialInfo.PeopleNearBy.length > 0
            ? [...socialInfo.PeopleNearBy, you].sort((a, b) => b.CurrentWeekProgress - a.CurrentWeekProgress)
            : [];

        setFriendsWithYou([...friends.slice(0, 3), more]);
        setPeopleNearByWithYou([...people.slice(0, 3), more]);
    }, [socialInfo, you, more]);

    return (
        <section className={styles.friendSection}>
            <h2 className='homeTitle'>{t('Get Social')}</h2>
            <p dir={i18n.dir()} className='homeSubtitle'>{t('Compete with friends, and community legends!')}</p>
            <div className={styles.friendsContainer}>
                <div className={styles.tabsContainer}>
                    <div className={styles.tab + ' ' + (selectedTab === 'friends' ? styles.active : '')}
                        onClick={() => setSelectedTab('friends')}>{t('Friends League')}</div>
                    <div className={styles.tab + ' ' + (selectedTab === 'peopleNearBy' ? styles.active : '')}
                        onClick={() => setSelectedTab('peopleNearBy')}>{t('People Nearby')}</div>
                </div>
                <hr />
                <div className={styles.friendsList}>
                    {selectedTab === 'friends' ? friendsWithYou?.map((friend, index) => (
                        <FriendsItem key={index} friend={friend} />
                    )) : peopleNearByWithYou?.map((friend, index) => (
                        <FriendsItem key={index} friend={friend} />
                    ))}
                </div>
            </div>
        </section>
    );
};
